import { faArrowRight, faCalendar, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const RecentArticles = () => {
    // Refs for animation targets
    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const articlesRef = useRef(null);
    const buttonRef = useRef(null);
    const articleCardsRef = useRef([]);

    const articles = [
        {
            id: 1,
            title: "Introduction to Machine Learning Algorithms",
            excerpt: "Explore the basics of machine learning algorithms and their applications in data science...",
            date: "September 5, 2024",
            readTime: "7 min read",
            category: "Machine Learning"
        },
        {
            id: 2,
            title: "Building Scalable Java Applications",
            excerpt: "Discover best practices for creating scalable and maintainable Java apps...",
            date: "June 2, 2024",
            readTime: "7 min read",
            category: "Development"
        },
        {
            id: 3,
            title: "Machine Learning in Python: A Beginner's Guide",
            excerpt: "Get started with machine learning using Python and popular libraries...",
            date: "June 20, 2024",
            readTime: "10 min read",
            category: "Programming"
        }
    ];

    // Set up animations
    useEffect(() => {
        // Reset card refs array
        articleCardsRef.current = [];
        
        // Main section fade in
        gsap.fromTo(
            sectionRef.current,
            { opacity: 0 },
            { 
                opacity: 1, 
                duration: 1,
                ease: "power2.out",
            }
        );

        // Section title animation
        gsap.fromTo(
            titleRef.current,
            { y: 50, opacity: 0 },
            { 
                y: 0, 
                opacity: 1, 
                duration: 1,
                ease: "back.out(1.7)",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top bottom-=100",
                    toggleActions: "play none none none"
                }
            }
        );

        // Staggered animation for article cards
        gsap.fromTo(
            articleCardsRef.current,
            { 
                y: 60, 
                opacity: 0,
                scale: 0.95,
            },
            { 
                y: 0, 
                opacity: 1,
                scale: 1,
                duration: 0.8,
                stagger: 0.15,
                ease: "back.out(1.5)",
                scrollTrigger: {
                    trigger: articlesRef.current,
                    start: "top bottom-=50",
                    toggleActions: "play none none none"
                }
            }
        );

        // Button animation
        gsap.fromTo(
            buttonRef.current,
            { 
                y: 30, 
                opacity: 0,
            },
            { 
                y: 0, 
                opacity: 1,
                duration: 0.7,
                delay: 0.3,
                ease: "back.out(1.7)",
                scrollTrigger: {
                    trigger: buttonRef.current,
                    start: "top bottom-=50",
                    toggleActions: "play none none none"
                }
            }
        );
    }, []);

    // Add each article card ref to the array
    const addToRefs = (el) => {
        if (el && !articleCardsRef.current.includes(el)) {
            articleCardsRef.current.push(el);
        }
    };

    return (
        <section 
            ref={sectionRef}
            className="py-20 bg-white dark:bg-gray-900"
            id="articles"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-4xl font-bold mb-4">
                        <span className="bg-gradient-to-r from-[#8f00ff] to-[#ffd700] bg-clip-text text-transparent">
                            Recent Articles
                        </span>
                    </h2>
                    <div className="w-24 h-1.5 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] rounded-full mb-6 mx-auto"></div>
                </div>
                
                <div ref={articlesRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {articles.map((article) => (
                        <article 
                            key={article.id}
                            ref={addToRefs}
                            className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden transform hover:scale-105 transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:border-[#8f00ff] dark:hover:border-[#ffd700] shadow-md"
                        >
                            <div className="p-6">
                                <div className="text-[#8f00ff] dark:text-[#ffd700] text-sm mb-3">{article.category}</div>
                                <h3 className="font-semibold text-xl mb-3 text-gray-800 dark:text-gray-200 group-hover:text-[#8f00ff] dark:group-hover:text-[#ffd700] transition-colors duration-300">
                                    {article.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">{article.excerpt}</p>
                                <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
                                    <span className="flex items-center">
                                        <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                                        {article.date}
                                    </span>
                                    <span className="flex items-center">
                                        <FontAwesomeIcon icon={faClock} className="mr-2" />
                                        {article.readTime}
                                    </span>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
                
                <div className="text-center mt-16">
                    <Link 
                        ref={buttonRef}
                        to="/articles"
                        className="group inline-flex items-center px-7 py-3.5 rounded-full overflow-hidden relative bg-gradient-to-r from-[#8f00ff] to-[#ffd700] text-white font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 shadow-md"
                    >
                        <span className="relative z-10 flex items-center">
                            View All Articles
                            <FontAwesomeIcon 
                                icon={faArrowRight} 
                                className="ml-2 transform group-hover:translate-x-1 transition-transform duration-300" 
                            />
                        </span>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default RecentArticles;